import Card from '@orn/shared-web/components/Card';
import { AlertTriangleIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { CalloutPriority } from '../../../../__generated__/globalTypes';
import { formatCalendarDate, toCurrency } from '../../../../utils/formatting';
import MerchantNameAndAvatar from '../../../MerchantNameAndAvatar';
import CalloutDismissConfirmation from '../CalloutDismissConfirmation';

import type { Callout } from '../../CalloutCard';

interface IUnusualTransactionMetadata {
  subtype: 'global' | 'merchant' | 'new_merchant';
  percentile: number;
  merchantPercentile?: number;
  merchantCount?: number;
}

interface IUnusualTransactionCalloutCardProps {
  callout: Callout;
  /**
   * Called when the user dismisses the callout.
   * @param appropriate - true if helpful, false if not helpful, undefined if not asked
   */
  onDismiss?: (appropriate?: boolean) => void;
  className?: string;
  onClick?: () => void;
  /**
   * 'full' shows transaction details (merchant, amount, date), used in carousel.
   * 'compact' shows description only, used in slideout.
   * @default 'full'
   */
  variant?: 'full' | 'compact';
}

export function isUnusualTransactionMetadata(metadata: unknown): metadata is IUnusualTransactionMetadata {
  if (typeof metadata !== 'object' || metadata === null || !('subtype' in metadata) || !('percentile' in metadata)) {
    return false;
  }

  const subtype = metadata.subtype;
  const percentile = metadata.percentile;

  if (subtype !== 'global' && subtype !== 'merchant' && subtype !== 'new_merchant') {
    return false;
  }

  if (typeof percentile !== 'number') {
    return false;
  }

  if ('merchantCount' in metadata && metadata.merchantCount != null && typeof metadata.merchantCount !== 'number') {
    return false;
  }

  // For 'merchant' subtype, merchantPercentile is required
  if (subtype === 'merchant') {
    return 'merchantPercentile' in metadata && typeof metadata.merchantPercentile === 'number';
  }

  // For other subtypes, merchantPercentile is optional but must be a number if present
  return !('merchantPercentile' in metadata) || typeof metadata.merchantPercentile === 'number';
}

const SMALL_MERCHANT_COUNT_THRESHOLD = 5;
const MERCHANT_PERCENT_THRESHOLD = 15;

const bold = (text: string) => <span className="text-primary font-bold">{text}</span>;

function getTopPercent(percentile: number): number {
  return Math.max(1, Math.min(99, 100 - Math.round(percentile)));
}

function getDescription(metadata: IUnusualTransactionMetadata, merchantName?: string): React.ReactNode {
  const { subtype, percentile, merchantPercentile, merchantCount } = metadata;
  const topGlobal = getTopPercent(percentile);
  const topMerchant = getTopPercent(merchantPercentile ?? 0);
  const merchantText = merchantName ?? 'this merchant';
  const merchant = <span className="inline-block max-w-56 truncate align-bottom">{merchantText}</span>;

  switch (subtype) {
    case 'merchant':
      if (
        merchantCount !== undefined &&
        Number.isFinite(merchantCount) &&
        merchantCount <= SMALL_MERCHANT_COUNT_THRESHOLD
      ) {
        return (
          <>
            This is a {bold('large')} transaction at {merchant}.
          </>
        );
      }

      if (topMerchant > MERCHANT_PERCENT_THRESHOLD) {
        return <>This is in the top {bold(`${topGlobal}%`)} of transactions.</>;
      }

      if (topMerchant < 2) {
        return (
          <>
            This is {bold('among the largest')} transactions at {merchant}.
          </>
        );
      }

      return (
        <>
          This is in the top {bold(`${topMerchant}%`)} of transactions at {merchant}.
        </>
      );
    case 'new_merchant':
      return (
        <>
          This is the first transaction at {merchant} and is in the top {bold(`${topGlobal}%`)} of transactions.
        </>
      );
    default:
      return <>This is in the top {bold(`${topGlobal}%`)} of transactions.</>;
  }
}

const HEADER_LABEL = 'Unusual amount';

const UnusualTransactionCalloutCard = ({
  callout,
  onDismiss,
  className,
  onClick,
  variant = 'full',
}: IUnusualTransactionCalloutCardProps) => {
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  const handleInitiateDismiss = React.useCallback(() => {
    setShowConfirmation(true);
  }, []);

  const handleConfirmDismiss = React.useCallback(
    (appropriate: boolean) => {
      onDismiss?.(appropriate);
    },
    [onDismiss]
  );

  // For full variant, require transaction
  if (variant === 'full' && !callout.transaction) {
    return null;
  }

  // Validate and extract metadata
  if (!isUnusualTransactionMetadata(callout.metadata)) {
    return null;
  }

  const transaction = callout.transaction;
  const merchant = callout.merchant;
  const amount = transaction ? Number(transaction.amount) : 0;
  const isNegative = amount < 0;
  const isP0 = callout.priority === CalloutPriority.P0;
  const metadata = callout.metadata;
  // If subtype is 'new_merchant' but there's no merchant, treat as 'global'
  const effectiveSubtype = metadata.subtype === 'new_merchant' && !merchant ? 'global' : metadata.subtype;
  const effectiveMetadata = { ...metadata, subtype: effectiveSubtype };
  const merchantName = merchant?.name;
  const description = getDescription(effectiveMetadata, merchantName);

  // Format the transaction date - only used in 'full' variant
  const dateDisplay =
    transaction && 'date' in transaction && transaction.date ? formatCalendarDate(transaction.date, 'medium') : '';

  // Confirmation state - show the confirmation UI
  if (showConfirmation) {
    return (
      <CalloutDismissConfirmation
        variant={isP0 ? 'error' : 'warning'}
        className={className}
        onConfirm={handleConfirmDismiss}
        headerIcon={<AlertTriangleIcon />}
        headerLabel={HEADER_LABEL}
        question="Did flagging this transaction help you?"
      />
    );
  }

  // Normal state - show the callout card
  return (
    <Card variant={isP0 ? 'error' : 'warning'} onClick={onClick} className={className}>
      <Card.Header
        icon={<AlertTriangleIcon />}
        label={HEADER_LABEL}
        onDismiss={onDismiss ? handleInitiateDismiss : undefined}
        dismissTooltip="Dismiss notification"
      />
      {variant === 'full' && transaction ? (
        <Card.Body>
          {/* Transaction Row */}
          <div className="flex items-start justify-between">
            <MerchantNameAndAvatar
              displayNameClassName="font-semibold"
              merchant={merchant}
              fallbackDescription={transaction.description}
              secondaryText={dateDisplay}
            />
            <span
              className={`text-md ml-2 font-semibold ${isNegative ? 'text-success-primary' : 'text-error-primary'}`}
            >
              {toCurrency(Math.abs(amount), { showSign: isNegative })}
            </span>
          </div>
          {/* Details */}
          <div className="flex flex-1 items-end">
            <Card.Text className="line-clamp-3">{description}</Card.Text>
          </div>
        </Card.Body>
      ) : (
        <Card.Body>
          <Card.Text>{description}</Card.Text>
        </Card.Body>
      )}
    </Card>
  );
};

export default UnusualTransactionCalloutCard;
